/**
 * BadgeEarnedModal - Celebration modal shown when user earns a badge
 *
 * Displays a brief, warm acknowledgment of the achievement
 * without being overly gamified or pressuring.
 */
import React, { useEffect, useRef, useState } from 'react';
import { isWeb } from '../util/platform';
import { View, Modal, Pressable, StyleSheet, Animated, AccessibilityInfo, Image } from 'react-native'
import * as Haptics from 'expo-haptics';
import CustomText from './CustomText';
import AppStyles from '../AppStyles';
import { getBadgeImage, BADGE_COLORS } from '../util/badgeUtils';
import { useTranslation } from '../i18n';

/**
 * BadgeEarnedModal Component
 *
 * @param {Object} props
 * @param {boolean} props.visible - Whether modal is visible
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Object} props.badge - Badge object { name, subtitle, color }
 */
const BadgeEarnedModal = ({
 visible, onClose, badge }) => {
  const { t } = useTranslation('badges');
  const scaleAnim = useRef(new Animated.Value(0.8)).current;
  const continueButtonRef = useRef(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check for reduced motion preference (WCAG AAA)
  useEffect(() => {
    const checkMotionPreference = async () => {
      const isReduceMotionEnabled = await AccessibilityInfo.isReduceMotionEnabled();
      setPrefersReducedMotion(isReduceMotionEnabled);
    };
    checkMotionPreference();

    const subscription = AccessibilityInfo.addEventListener('reduceMotionChanged', (isEnabled) => {
      setPrefersReducedMotion(isEnabled);
    });

    return () => subscription?.remove();
  }, []);

  // Trigger animation and haptics when modal becomes visible
  useEffect(() => {
    if (visible && badge) {
      // Haptic feedback (mobile only)
      if (!isWeb()) {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      }

      // Scale animation
      if (prefersReducedMotion) {
        scaleAnim.setValue(1);
      } else {
        scaleAnim.setValue(0.8);
        Animated.spring(scaleAnim, {
          toValue: 1,
          useNativeDriver: true,
          friction: 8,
          tension: 100,
        }).start();
      }

      // Focus the Continue button for accessibility after animation settles
      const focusTimer = setTimeout(() => {
        if (continueButtonRef.current) {
          AccessibilityInfo.setAccessibilityFocus(continueButtonRef.current);
        }
      }, prefersReducedMotion ? 100 : 400);

      return () => clearTimeout(focusTimer);
    }
  }, [visible, badge, prefersReducedMotion, scaleAnim]);

  if (!badge) return null;

  const badgeColor = BADGE_COLORS[badge.color]?.icon || BADGE_COLORS.gold.icon;

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent={true}
      onRequestClose={onClose}
    >
      <Pressable style={styles.overlay} onPress={onClose}>
        <View
          style={styles.modalContainer}
          accessibilityRole="alert"
          accessibilityViewIsModal={true}
          accessibilityLabel={t('badgeEarnedLabel', { badge: badge.name, subtitle: badge.subtitle })}
        >
          {/* Badge Icon */}
          <Animated.View
            style={[
              styles.iconContainer,
              { transform: [{ scale: scaleAnim }] },
            ]}
            accessibilityRole="image"
            accessibilityLabel={`${badge.name} badge icon`}
          >
            {getBadgeImage(badge.id) ? (
              <Image
                source={getBadgeImage(badge.id)}
                style={styles.badgeImage}
              />
            ) : (
              <View style={[styles.iconCircleFallback, { backgroundColor: badgeColor }]}>
                <CustomText font="bold" size={32} color="#FFFFFF">
                  {'\u2605'}
                </CustomText>
              </View>
            )}
          </Animated.View>

          {/* Celebration Text */}
          <CustomText
            font="semiBold"
            size={20}
            color={AppStyles.colorSet.textPrimary}
            style={styles.title}
          >
            {t('earnedOne')}
          </CustomText>

          {/* Badge Name */}
          <View style={[styles.badgeNameContainer, { borderColor: badgeColor }]}>
            <CustomText
              font="semiBold"
              size={16}
              color={badgeColor}
            >
              {badge.name}
            </CustomText>
            <CustomText
              font="regular"
              size={13}
              color={AppStyles.colorSet.textSecondary}
              style={styles.subtitle}
            >
              {badge.subtitle}
            </CustomText>
          </View>

          {/* Thank you message */}
          <CustomText
            font="regular"
            size={14}
            color={AppStyles.colorSet.textSecondary}
            style={styles.message}
          >
            {t('thankYouMessage')}
          </CustomText>

          {/* Continue Button */}
          <Pressable
            ref={continueButtonRef}
            style={({ pressed }) => [
              styles.continueButton,
              { backgroundColor: badgeColor },
              pressed && styles.continueButtonPressed,
            ]}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel={t('continueLabel')}
            accessibilityHint={t('closeHint')}
          >
            <CustomText font="medium" size={16} color="#FFFFFF">
              {t('continue')}
            </CustomText>
          </Pressable>
        </View>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: AppStyles.spacing.xl,
  },
  modalContainer: {
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderRadius: AppStyles.borderRadii.lg,
    width: '100%',
    maxWidth: 340,
    padding: AppStyles.spacing.xxl,
    alignItems: 'center',
    ...AppStyles.styleSet.shadowLarge,
  },
  iconContainer: {
    width: 120,
    height: 120,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: AppStyles.spacing.lg,
  },
  badgeImage: {
    width: 120,
    height: 120,
    resizeMode: 'contain',
  },
  iconCircleFallback: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    marginBottom: AppStyles.spacing.lg,
    textAlign: 'center',
  },
  badgeNameContainer: {
    paddingVertical: AppStyles.spacing.md,
    paddingHorizontal: AppStyles.spacing.xl,
    borderRadius: AppStyles.borderRadii.md,
    borderWidth: 2,
    alignItems: 'center',
    marginBottom: AppStyles.spacing.lg,
  },
  subtitle: {
    marginTop: 4,
  },
  message: {
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: AppStyles.spacing.xl,
  },
  continueButton: {
    paddingVertical: AppStyles.spacing.md,
    paddingHorizontal: AppStyles.spacing.xxxl,
    borderRadius: AppStyles.borderRadii.md,
  },
  continueButtonPressed: {
    opacity: 0.8,
  },
});

export default BadgeEarnedModal;
